import React, { useEffect, useMemo, useState } from 'react';
import { useAdminAzucarGlass } from '../../hooks/useAdminAzucarGlass.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const buildDraft = (item) => ({
  ...item,
  equivalencia: item.equivalencia ?? ''
});

const normalizeEquivalencia = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isRowDirty = (draft, original) => {
  if (!original) {
    return true;
  }
  const originalEquiv = normalizeEquivalencia(original.equivalencia);
  const draftEquiv = normalizeEquivalencia(draft.equivalencia);
  return (
    Boolean(draft.usar_forecast) !== Boolean(original.usar_forecast)
    || Boolean(draft.usar_equivalencia) !== Boolean(original.usar_equivalencia)
    || Boolean(draft.es_base) !== Boolean(original.es_base)
    || originalEquiv !== draftEquiv
  );
};

const AzucarGlassSection = ({ onNotify }) => {
  const {
    items,
    loading,
    error,
    refresh,
    saveItem
  } = useAdminAzucarGlass();

  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [rowStatus, setRowStatus] = useState({});

  useLucideIcons([items.length, search, loading]);

  useEffect(() => {
    setDrafts(items.map(buildDraft));
  }, [items]);

  const itemsById = useMemo(() => (
    items.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {})
  ), [items]);

  const filteredDrafts = useMemo(() => {
    if (!search) {
      return drafts;
    }
    const query = search.toLowerCase();
    return drafts.filter((item) => {
      const name = item.nombre?.toLowerCase() || '';
      const process = item.proceso_nombre?.toLowerCase() || '';
      const bohId = item.articulo_id_boh?.toString().toLowerCase() || '';
      return name.includes(query) || process.includes(query) || bohId.includes(query);
    });
  }, [drafts, search]);

  const updateDraft = (id, updater) => {
    setDrafts((prev) => prev.map((item) => (
      item.id === id ? { ...item, ...updater(item) } : item
    )));
  };

  const handleToggleForecast = (id) => {
    updateDraft(id, (item) => (
      item.es_base
        ? item
        : {
          usar_forecast: true,
          usar_equivalencia: false,
          es_base: false
        }
    ));
  };

  const handleToggleEquivalencia = (id) => {
    updateDraft(id, (item) => (
      item.es_base
        ? item
        : {
          usar_forecast: false,
          usar_equivalencia: true,
          es_base: false,
          equivalencia: item.equivalencia === '' ? 1 : item.equivalencia
        }
    ));
  };

  const handleToggleBase = (id) => {
    setDrafts((prev) => prev.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          es_base: true,
          usar_forecast: true,
          usar_equivalencia: false,
          equivalencia: ''
        };
      }
      return { ...item, es_base: false };
    }));
  };

  const handleEquivalenciaChange = (id, value) => {
    updateDraft(id, () => ({
      equivalencia: value
    }));
  };

  const handleSave = async (draft) => {
    const status = rowStatus[draft.id];
    if (status?.saving) {
      return;
    }
    setRowStatus((prev) => ({ ...prev, [draft.id]: { saving: true, error: null } }));
    try {
      const saved = await saveItem(draft.id, draft);
      if (saved) {
        setDrafts((prev) => prev.map((item) => (
          item.id === draft.id ? buildDraft(saved) : item
        )));
      }
      refresh();
      onNotify?.({ type: 'success', message: `Configuración guardada para ${draft.nombre}.` });
      setRowStatus((prev) => ({ ...prev, [draft.id]: { saving: false, error: null } }));
    } catch (err) {
      setRowStatus((prev) => ({ ...prev, [draft.id]: { saving: false, error: err?.message } }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar la configuración.' });
    }
  };

  return (
    <section id="azucar-glass-section" className="admin-section active">
      <div className="section-header">
        <div className="section-title-group">
          <div className="section-eyebrow">Procesos Azucar Glass</div>
          <h2 className="section-title">Azúcar Glass</h2>
          <p className="section-subtitle">
            Define si cada artículo usará AI.FORECAST o una equivalencia por unidad pronosticada.
          </p>
        </div>
        <div className="section-actions">
          <div className="search-container">
            <span className="search-icon"><i data-lucide="search" /></span>
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por artículo, proceso o ID BoH..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search ? (
              <button type="button" className="btn-secondary" onClick={() => setSearch('')}>
                Limpiar
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando artículos...</div>
          </div>
        ) : (
          <table className="data-table azucar-glass-table" id="azucar-glass-table">
            <thead>
              <tr>
                <th>Artículo</th>
                <th>Proceso</th>
                <th>ID BoH</th>
                <th>Unidad</th>
                <th>AI.FORECAST</th>
                <th>Equivalencia</th>
                <th>Factor</th>
                <th>Base</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={9} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                    <div className="empty-state-text">{error}</div>
                  </td>
                </tr>
              ) : filteredDrafts.length === 0 ? (
                <tr>
                  <td colSpan={9} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="package" /></div>
                    <div className="empty-state-text">No hay artículos con proceso Azucar Glass.</div>
                    <div className="empty-state-subtext">Marca procesos como Azucar Glass para verlos aquí.</div>
                  </td>
                </tr>
              ) : (
                filteredDrafts.map((draft) => {
                  const original = itemsById[draft.id];
                  const dirty = isRowDirty(draft, original);
                  const status = rowStatus[draft.id] || {};
                  return (
                    <tr key={draft.id}>
                      <td>{draft.nombre}</td>
                      <td>{draft.proceso_nombre || '-'}</td>
                      <td>{draft.articulo_id_boh || '-'}</td>
                      <td>{draft.unidades || '-'}</td>
                      <td>
                        <label className="checkbox-item checkbox-small">
                          <input
                            type="checkbox"
                            checked={Boolean(draft.usar_forecast)}
                            onChange={() => handleToggleForecast(draft.id)}
                            disabled={draft.es_base}
                          />
                          <span className="checkmark" />
                          <span className="checkbox-label">Usar AI.FORECAST</span>
                        </label>
                      </td>
                      <td>
                        <label className="checkbox-item checkbox-small">
                          <input
                            type="checkbox"
                            checked={Boolean(draft.usar_equivalencia)}
                            onChange={() => handleToggleEquivalencia(draft.id)}
                            disabled={draft.es_base}
                          />
                          <span className="checkmark" />
                          <span className="checkbox-label">Usar equivalencia</span>
                        </label>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-input equivalence-input"
                          min="0"
                          step="0.01"
                          value={draft.equivalencia}
                          onChange={(event) => handleEquivalenciaChange(draft.id, event.target.value)}
                          disabled={!draft.usar_equivalencia || draft.es_base}
                        />
                      </td>
                      <td>
                        <label className="checkbox-item checkbox-small">
                          <input
                            type="checkbox"
                            checked={Boolean(draft.es_base)}
                            onChange={() => handleToggleBase(draft.id)}
                          />
                          <span className="checkmark" />
                          <span className="checkbox-label">Base</span>
                        </label>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn"
                            type="button"
                            onClick={() => handleSave(draft)}
                            disabled={!dirty || status.saving}
                            title={status.error || (dirty ? 'Guardar cambios' : 'Sin cambios')}
                          >
                            <i data-lucide="save" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

export default AzucarGlassSection;
